/**
 * Carrying a destination across the password reset chain.
 *
 * The reset email lands here and the visitor then makes three hops before they are signed in:
 *
 *     /login/token/:token  ->  /passwordReset  ->  /login  ->  (they log in)
 *
 * The destination has to survive all three, and it rides on the URL rather than in the
 * `passwordReset` redux slice so that a reload does not lose it. (The *token* does live in that
 * slice, which is why reloading `/passwordReset` already bounces to `/login` — not fixed here, but
 * not extended either.)
 *
 * The last hop needs nothing from us: `/login?redirect=...` on a logged-out page is already picked
 * up by `syncPostAuthDestinationForNavigation`, which is what `goToPostAuthDestination` reads after
 * the login completes.
 *
 * ## Why not just rely on the cookie
 *
 * We could, and until now we did — but only for a visitor who opens the email in the same browser.
 * The post-auth destination cookie is absent when the mail is read on another device, which is the
 * ordinary case, and it is overwritten by anything the visitor browses on zl-app while waiting.
 * A destination written into the link itself is immune to both.
 */

import { withQueryParam } from './destinationURL';

/** The parameter the whole chain agrees on. Also what zl-app's `/login` already reads. */
export const REDIRECT_PARAM = 'redirect';

/**
 * Add a destination to a path, preserving any query the path already has.
 *
 * The destination itself routinely carries a query — `/educators/classroom?sf=...` — which is why
 * this goes through `withQueryParam` rather than concatenating. See that module for what the naive
 * version costs.
 */
export const withRedirect = (path: string, redirect: string | null | undefined): string =>
  redirect ? withQueryParam(path, REDIRECT_PARAM, redirect) : path;

/** Read the destination off the current location, for a hop that has to pass it on. */
export const readRedirect = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return new URLSearchParams(window.location.search).get(REDIRECT_PARAM);
};
